/**
 * Central TanStack Query key factory.
 *
 * Use these instead of inline arrays so keys stay consistent and are easy to
 * invalidate. Each leaf returns a `readonly` tuple. Pattern (extend per module
 * in later phases):
 *
 *   queryKeys.users.list({ q: 'jane' })
 *   queryKeys.users.details(42)
 *   queryKeys.timeline.list(0)
 *
 * `params` objects are spread into the key as-is; pass a stable, serialisable
 * object so React Query can compare keys structurally.
 */

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    /** Current authenticated user (`/auth/verify`). */
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  counts: {
    all: ['counts'] as const,
    /** Per-user unread / pending counts (`/user/counts`). */
    me: () => [...queryKeys.counts.all, 'me'] as const,
  },

  users: {
    all: ['users'] as const,
    /** Paginated / searchable user list (`/user/list`). */
    list: (params?: object) =>
      [...queryKeys.users.all, 'list', params ?? {}] as const,
    /**
     * Infinite People-page list (`/user/list?type=latest&page=…&q=…&filters[…]=…`).
     * Distinct from `list` so the People page's infinite cache doesn't collide
     * with the showcase widgets' single-page `useUserList` queries.
     */
    people: (params?: object) =>
      [...queryKeys.users.all, 'people', params ?? {}] as const,
    /** Full profile for a single user (`/user/details/{id}`). */
    details: (id: number | string) =>
      [...queryKeys.users.all, 'details', id] as const,
    /** A user's friends (`/user/friends/{id}`) — single page. */
    friends: (id: number | string, params?: object) =>
      [...queryKeys.users.all, 'friends', id, params ?? {}] as const,
    /**
     * Infinite friends list (`/user/friends/{id}?page=…&q=…`). Distinct from
     * `friends` so the Friends-page infinite cache doesn't collide with the
     * AboutTab / FriendsTab single-page reads.
     */
    friendsInfinite: (id: number | string, params?: object) =>
      [...queryKeys.users.all, 'friends-infinite', id, params ?? {}] as const,
    /**
     * Incoming friend requests for a user (`/user/friend-requests/{id}`).
     * The no-params form returns a 3-element key — a TRUE PREFIX of the
     * with-params form — so `invalidateQueries({ queryKey: friendRequests(id) })`
     * matches the actual cached query (registered with `{ limit: 24 }`).
     * Without this, the prior `params ?? {}` trailing element caused a
     * silent prefix mismatch: invalidating with `{}` couldn't match a
     * query stored with `{ limit: 24 }`, so the list never refreshed
     * after Confirm/Delete actions.
     */
    friendRequests: (id: number | string, params?: object) =>
      params !== undefined
        ? ([...queryKeys.users.all, 'friend-requests', id, params] as const)
        : ([...queryKeys.users.all, 'friend-requests', id] as const),
    /** A user's profile visitors (`/user/visitors/{id}`). */
    visitors: (id: number | string, params?: object) =>
      [...queryKeys.users.all, 'visitors', id, params ?? {}] as const,
    /** A user's media — photos/videos (`/user/media/{id}`). */
    media: (id: number | string, params?: object) =>
      [...queryKeys.users.all, 'media', id, params ?? {}] as const,
    /** A user's profile photos (`/user/photos/get/{id}`). */
    photos: (id: number | string) =>
      [...queryKeys.users.all, 'photos', id] as const,
    /** A user's blogs (`/blogs/list?user_id={id}`). */
    blogs: (id: number | string, params?: object) =>
      [...queryKeys.users.all, 'blogs', id, params ?? {}] as const,
    /**
     * Nearby-user widget (`/user/list?type=nearest&lat=&lng=&limit=`).
     * Distinct from `list` so the small widget cache doesn't collide with
     * larger paginated reads.
     */
    nearby: (params?: object) =>
      [...queryKeys.users.all, 'nearby', params ?? {}] as const,
  },

  timeline: {
    all: ['timeline'] as const,
    /** Activity feed for a user (`/timeline/list/{id}`; 0 = main feed). */
    list: (id: number | string, params?: object) =>
      [...queryKeys.timeline.all, 'list', id, params ?? {}] as const,
    /** A single activity item (`/timeline/get/{id}`). */
    detail: (id: number | string) =>
      [...queryKeys.timeline.all, 'detail', id] as const,
    /** Posts for a hashtag (`/timeline/hashtag/{tag}`). */
    hashtag: (tag: string, params?: object) =>
      [...queryKeys.timeline.all, 'hashtag', tag, params ?? {}] as const,
    /** Public marketing-homepage showcase (`/timeline/public-showcase`). */
    publicShowcase: (params?: object) =>
      [...queryKeys.timeline.all, 'public-showcase', params ?? {}] as const,
    /** Trending hashtags (`/timeline/hashtag/list`). */
    trending: (params?: object) =>
      [...queryKeys.timeline.all, 'trending', params ?? {}] as const,
    /** Comments (or likes) for an activity (`/timeline/{id}/items/{type}`). */
    items: (id: number | string, itemType: 'comments' | 'likes') =>
      [...queryKeys.timeline.all, 'items', id, itemType] as const,
  },

  blogs: {
    all: ['blogs'] as const,
    /** Blog categories (`/blogs/categories`). */
    categories: () => [...queryKeys.blogs.all, 'categories'] as const,
    /** Paginated blog list (`/blogs/list`); `params` carries category/q/myblogs. */
    list: (params?: object) =>
      [...queryKeys.blogs.all, 'list', params ?? {}] as const,
    /** A single blog (`/blogs/view/:slug`). */
    detail: (slug: string) =>
      [...queryKeys.blogs.all, 'detail', slug] as const,
  },

  news: {
    all: ['news'] as const,
    /** Paginated news list (`/news/list`); `params` carries country. */
    list: (params?: object) =>
      [...queryKeys.news.all, 'list', params ?? {}] as const,
    /** A single news article (`/news/list?slug=...`), keyed by country + slug. */
    detail: (country: string, slug: string) =>
      [...queryKeys.news.all, 'detail', country, slug] as const,
  },

  chat: {
    all: ['chat'] as const,
    /** Conversation list (`/chat/list`). `q` = search filter. */
    list: (params?: object) =>
      [...queryKeys.chat.all, 'list', params ?? {}] as const,
    /** A thread with one participant, keyed by username (`/chat/get/:username`). */
    thread: (username: string) =>
      [...queryKeys.chat.all, 'thread', username] as const,
  },

  livestream: {
    all: ['livestream'] as const,
    /** Chat poll-fallback for a single stream (`/livestream-v2/chat/:streamId`). */
    chat: (streamId: number | string) =>
      [...queryKeys.livestream.all, 'chat', streamId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    /** Paginated notifications for a user (`/user/notifications/:id`). */
    list: (id: number | string, params?: object) =>
      [...queryKeys.notifications.all, 'list', id, params ?? {}] as const,
  },

  pokes: {
    all: ['pokes'] as const,
    /** Received pokes for a user (`/user/pokes/:id`). */
    list: (id: number | string) =>
      [...queryKeys.pokes.all, 'list', id] as const,
  },

  invite: {
    all: ['invite'] as const,
    /** Viewer's referral code + shareable link (`GET invite/code`). */
    code: () => [...queryKeys.invite.all, 'code'] as const,
    /** Counts + recent invitees for the viewer (`GET invite/stats`). */
    stats: () => [...queryKeys.invite.all, 'stats'] as const,
  },

  subscription: {
    all: ['subscription'] as const,
    /** Public subscription plans + gateway credentials (`GET subscription/plans`). */
    plans: (params?: object) =>
      [...queryKeys.subscription.all, 'plans', params ?? {}] as const,
  },
} as const;
